import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, take, tap } from 'rxjs';
import { Store, select } from '@ngrx/store';
import {
  createPostQuickStoryReport,
  updatePostQuickStoryReport,
} from '../../../actions/post.actions';
import {
  selectFeed,
  selectPostById,
  selectPostLoading,
  selectQuickStoryReportLoading,
} from 'src/app/core/reducers';

import { AppState } from 'src/app/reducers';
import { FormsService } from 'src/app/core/services/forms.service';
import { NomLanguages } from 'src/app/core/constants';
import { Post } from 'src/app/core/models/core.models';
import { UtilsService } from 'src/app/core/services/utils.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-post-quick-story',
  templateUrl: './post-quick-story.component.html',
  styleUrls: ['./post-quick-story.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostQuickStoryComponent {
  @Input() postId: string;

  feedId: string;
  post$: Observable<Post>;
  loadingQuickStoryReport$: Observable<boolean>;
  post: Post;
  initialReportContent: string;

  formGroup: FormGroup = new FormGroup({
    language: new FormControl('english', [Validators.required]),
    reportContent: new FormControl(),
  });

  protected utilService = inject(UtilsService);
  protected destroyRef = inject(DestroyRef);

  constructor(
    private store: Store<AppState>,
    private formService: FormsService,
  ) {
    //console.log('post full content');
    // @TODO languages to be list from formService
  }

  ngOnInit(): void {
    this.post$ = this.store.select(selectPostById(this.postId));
    this.loadingQuickStoryReport$ = this.store.select(
      selectQuickStoryReportLoading(this.postId),
    );
    this.store
      .pipe(
        select(selectFeed),
        tap((feed) => {
          this.feedId = feed.id;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.post$
      .pipe(
        tap((post) => {
          this.post = post;
          this.setReportContent();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  languageName: string = this.formGroup.get('language').value;

  getLanguageId(languageName: string) {
    switch (languageName) {
      case 'english':
        return NomLanguages.ENGLISH;
      case 'arabic':
        return NomLanguages.ARABIC;
      case 'bulgarian':
        return NomLanguages.BULGARIAN;
      default:
        return null;
    }
  }

  get currentLanguageId() {
    return this.getLanguageId(this.formGroup.get('language').value);
  }

  setReportContent() {
    this.initialReportContent =
      this.post?.quick_story_report[this.currentLanguageId]?.html;
    this.formGroup.patchValue({ reportContent: this.initialReportContent });
  }

  generateQuickStoryReport() {
    const languageName = this.formGroup.get('language').value;
    const languageId = this.getLanguageId(languageName);

    this.store.dispatch(
      createPostQuickStoryReport({
        postId: this.post.id,
        feedId: this.feedId,
        languageId: languageId,
      }),
    );
  }

  copyText() {
    const text = this.formGroup.get('reportContent')?.value;

    this.utilService.copyToClipboard(text);
  }

  updateReport() {
    const languageName = this.formGroup.get('language').value;
    const languageId = this.getLanguageId(languageName);
    const reportContent = this.formGroup.get('reportContent').value;

    this.store.dispatch(
      updatePostQuickStoryReport({
        postId: this.post.id,
        languageId: languageId,
        html: reportContent,
      }),
    );
  }
}
